import { requestJson } from './client'
import type { Account, WeChatProvider } from '../types/domain'

export interface AccountAccess {
  canAccessWorkspace: boolean
  account: Account | null
  wechatLoginAvailable: Record<WeChatProvider, boolean>
}

function validAccount(account: Account | null): account is Account {
  return !!account && typeof account.id === 'string' && !!account.id.trim()
    && typeof account.name === 'string' && !!account.name.trim()
    && typeof account.username === 'string' && !!account.username.trim()
    && typeof account.passwordLoginEnabled === 'boolean' && typeof account.wechatBound === 'boolean'
    && typeof account.wechatBindings?.web === 'boolean' && typeof account.wechatBindings?.oa === 'boolean'
    && account.wechatBound === (account.wechatBindings.web || account.wechatBindings.oa)
}

export async function readAccountAccess(): Promise<AccountAccess> {
  const value = await requestJson<AccountAccess>('/api/access')
  if (!value || typeof value.canAccessWorkspace !== 'boolean'
    || typeof value.wechatLoginAvailable?.web !== 'boolean' || typeof value.wechatLoginAvailable?.oa !== 'boolean'
    || (value.account !== null && (!value.account || typeof value.account.id !== 'string' || !value.account.id.trim()))) {
    throw new Error('暂时无法确认当前账号，请重试。')
  }
  if (value.canAccessWorkspace) {
    if (!validAccount(value.account)) {
      throw new Error('暂时无法确认当前账号，请重试。')
    }
  }
  return value
}

export async function saveAccountProfile(accountId: string, name: string): Promise<Account> {
  const account = await requestJson<Account>('/api/access/profile', { accountId, name })
  if (!validAccount(account) || account.id !== accountId || typeof account.active !== 'boolean'
    || !['admin', 'moderator', 'member'].includes(account.role)
    || Array.from(account.name).length > 40 || /[\u0000-\u001f\u007f\u0085\u2028\u2029]/u.test(account.name)) {
    throw new Error('昵称保存结果未能确认，请重新确认账号。')
  }
  return account
}

export async function startWeChatBinding(password: string, accountId: string, provider: WeChatProvider): Promise<string> {
  const result = await requestJson<{ url: string }>('/api/access/wechat/bind-start', { password, accountId, provider })
  if (!result || typeof result.url !== 'string' || !result.url) throw new Error('微信授权地址未能生成，请重试。')
  let destination: URL
  try { destination = new URL(result.url) } catch { throw new Error('微信授权地址无效，请重试。') }
  const expectedPath = provider === 'web' ? '/connect/qrconnect' : '/connect/oauth2/authorize'
  if (destination.protocol !== 'https:' || destination.hostname !== 'open.weixin.qq.com'
    || destination.pathname !== expectedPath || destination.username || destination.password || destination.port) {
    throw new Error('微信授权地址无效，请重试。')
  }
  return destination.href
}

export function logoutAccount(): Promise<unknown> {
  return requestJson('/api/access/logout', {})
}
